import * as React from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { Textarea } from './textarea';
import { Label } from './label';
import { Alert, AlertDescription } from './alert';

export interface ConfirmActionModalProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'default' | 'destructive';
  requireReason?: boolean;
  /**
   * Mostra il campo motivo anche quando non è obbligatorio. Quando true ma
   * `requireReason` è false, l'utente può scrivere un motivo opzionale (no
   * min length) che viene passato a `onConfirm`. Implicito se
   * `requireReason` è true.
   */
  showReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  minReasonLength?: number;
  maxReasonLength?: number;
  submitting?: boolean;
  errorMessage?: string | null;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  open,
  title,
  description,
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  confirmVariant = 'default',
  requireReason = false,
  showReason = false,
  reasonLabel = 'Motivo',
  reasonPlaceholder = 'Spiega il motivo dell\'azione (richiesto per audit log)',
  minReasonLength = 10,
  maxReasonLength = 500,
  submitting = false,
  errorMessage,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const showReasonField = requireReason || showReason;
  const reasonValid =
    !requireReason ||
    (trimmed.length >= minReasonLength && trimmed.length <= maxReasonLength);
  const disabled = submitting || !reasonValid;

  const handleConfirm = (): void => {
    if (disabled) return;
    void onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onCancel(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {showReasonField && (
          <div className="space-y-2">
            <Label htmlFor="confirm-action-reason">
              {reasonLabel}{requireReason && <span className="text-red-500"> *</span>}
            </Label>
            <Textarea
              id="confirm-action-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
              maxLength={maxReasonLength}
              disabled={submitting}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {requireReason
                  ? (trimmed.length < minReasonLength
                      ? `Almeno ${minReasonLength} caratteri`
                      : 'Motivo valido')
                  : 'Opzionale ma raccomandato per audit log'}
              </span>
              <span>{trimmed.length}/{maxReasonLength}</span>
            </div>
          </div>
        )}

        {errorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={disabled}
            className={confirmVariant === 'destructive' ? '' : 'bg-cyan-600 hover:bg-cyan-700'}
          >
            {submitting ? 'Attendere...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
